/**
 * useReviewerSession — shared SSE transport + lifecycle for the CLW
 * platform reviewer hooks (GroundTruth CTS and MMGY).
 *
 * Both reviewers talk to a backend that pushes the SAME digested event
 * vocabulary over `GET {routeBase}/:id/stream`:
 *   agent.message · suggestion.added · apply.failed · edit.staged ·
 *   session.status_{idle,running,rescheduling,terminated} · session.finalized ·
 *   usage · custom_tool_request · custom_tool_result · error
 * plus ONE platform-specific "plan updated" event
 * (review_cts.updated for GT, final_plan.updated for MMGY), and — for MMGY —
 * an extra approved_plan_gcs_path field on session.finalized.
 *
 * This hook owns everything shared: session create / connect / reconnect /
 * terminate / restart (newSession), history hydration, event dedup, status
 * mapping, usage accumulation, and the conversation-facing state. Platform
 * adapters (useGtCtsReviewerSession, useMmgyClwReviewerSession) are thin
 * wrappers that supply the route, the create-payload validation, the
 * auto-start readiness check, and a reducer for the platform-specific
 * event(s). They re-export the EXACT same return shape they had when
 * standalone, so no consumer changes are needed.
 *
 * NOTE: this is NOT the plan-review-page hook (hooks/useAgentSession.js). That
 * one has a different session model (parent-owned sessionId), event vocabulary
 * (raw tool events), and derived state — deliberately left separate.
 */

import { useCallback, useEffect, useRef, useState } from 'react';
import { api, getApiUrl } from '../utils/apiConfig';

export const REVIEWER_STATUS = {
  IDLE: 'idle',                       // hook not yet started
  CREATING: 'creating',               // POST / in flight
  INITIALIZING: 'initializing',       // session created, history loading
  STREAMING: 'streaming',             // SSE open, no current agent turn
  RUNNING: 'running',                 // agent is producing a turn
  WAITING_FOR_USER: 'waiting_for_user',
  FINALIZED: 'finalized',
  TERMINATED: 'terminated',
  ERROR: 'error',
};

const ZERO_USAGE = { input_tokens: 0, output_tokens: 0, cache_read_input_tokens: 0 };

export function useReviewerSession({
  accountId,
  userId,
  autoStart = false,
  createPayload,
  // ── platform configuration ──────────────────────────────────────────────
  routeBase,                  // e.g. '/api/v2/gt-cts-reviewer-sessions'
  logName = 'useReviewerSession',
  // (payload) => string | null — error message when the payload can't create a
  // session, else null. Drives createSession's guard.
  validateCreatePayload,
  // (payload) => boolean — whether auto-start should fire. May be stricter
  // than validateCreatePayload (e.g. GT also needs a review id).
  isAutoStartReady,
  // payload-derived values the auto-start effect re-runs on. Must be a
  // fixed-length array across renders.
  autoStartDeps = [],
  // OPTIONAL string identifying WHAT the live session is about (e.g.
  // `${campaignId}:${runId}`). When it changes while a session is open, that
  // session is torn down and a fresh one starts.
  //
  // Without it, auto-start bails on `if (sessionId) return` — so once any
  // session exists, changing the subject silently keeps the old conversation.
  // On the optimizer that meant clicking a second campaign left the first
  // campaign's conversation on screen while the new campaign's run was pushed
  // into it by refresh-run; the agent noticed mid-answer that "the run has
  // changed under us — this is now a different campaign entirely", and a user
  // asking to apply "the 8 high-priority suggestions" was talking about a run
  // that no longer existed.
  //
  // Opt-in, and a STRING on purpose. The existing autoStartDeps are not all
  // stable across renders (object/JSON payloads on the GT and MMGY surfaces),
  // so restarting on those would risk a create loop; a key the caller derives
  // is stable by construction. Surfaces that pass nothing behave exactly as
  // before.
  sessionKey = null,
  // initial platform-specific state — its keys become extra return fields
  // (latestReviewCts… for GT, latestFinalPlan… for MMGY).
  initialPlatformState = {},
  // (data) => partialPatch | ((prev) => partialPatch) | null — maps the
  // platform-specific event(s) to a platform-state patch (review_cts.updated /
  // final_plan.updated, plus any extra fields the platform layers onto
  // session.finalized). Return a FUNCTION when the patch depends on the current
  // platform state: it runs inside the state updater, so two events landing
  // before a re-render cannot overwrite each other.
  reducePlatformEvent,
  // (data) => partialPatch | null — maps the CREATE response onto platform
  // state. The bulk editor uses it to restore a pending Confirm card the
  // backend hands back on resume (pendingPreview), so a page reload no longer
  // loses an un-confirmed proposal.
  reduceCreateData,
}) {
  const STATUS = REVIEWER_STATUS;

  // ── shared state ──────────────────────────────────────────────────────────
  const [sessionId, setSessionId] = useState(null);
  const [messages, setMessages] = useState([]);
  const [suggestions, setSuggestions] = useState([]);
  const [status, setStatus] = useState(STATUS.IDLE);
  const [error, setError] = useState(null);
  const [connectionError, setConnectionError] = useState(null);
  const [approvedMarker, setApprovedMarker] = useState(null);
  const [usage, setUsage] = useState(ZERO_USAGE);
  const [artifactErrors, setArtifactErrors] = useState([]);
  const [interrupting, setInterrupting] = useState(false);
  const [pendingToolCalls, setPendingToolCalls] = useState(0);
  // Ref mirror so sendMessage's self-heal can read the CURRENT count without
  // re-creating the callback on every tool roundtrip.
  const pendingToolCallsRef = useRef(0);
  useEffect(() => { pendingToolCallsRef.current = pendingToolCalls; }, [pendingToolCalls]);
  const [lastApplyFailure, setLastApplyFailure] = useState(null);
  const [lastEditStaged, setLastEditStaged] = useState(null);
  const [platformState, setPlatformState] = useState(initialPlatformState);
  // Pinned to the first value: callers pass an object literal, so it is a new
  // object every render. Used to reset platform state when the session's
  // SUBJECT changes (see sessionKey) — unlike the manual "New session", which
  // deliberately preserves it so committed edits survive a restart. A preview
  // card or approved batch belonging to another campaign is not something to
  // preserve; it is the leak this exists to stop.
  const initialPlatformStateRef = useRef(initialPlatformState);
  const esRef = useRef(null);
  // Consecutive failed stream attempts — reset on a successful open. Drives
  // the automatic backoff in es.onerror.
  const reconnectAttempts = useRef(0);
  // Stream-event dedup keyed by `${type}:${id}` so a custom_tool_request and
  // custom_tool_result that intentionally share an id don't collide.
  const seenEventIds = useRef(new Set());

  // Latest platform callbacks held in a ref so the memoized create/connect
  // keep minimal, stable dep arrays — passing fresh inline callbacks from an
  // adapter each render must NOT tear down and reopen the EventSource.
  const policyRef = useRef(null);
  policyRef.current = { validateCreatePayload, isAutoStartReady, reducePlatformEvent, reduceCreateData };

  // ── History replay — hydrate messages + suggestions before the live tail ──
  const loadHistory = useCallback(async (id) => {
    if (!id) return;
    try {
      const [metaRes, historyRes] = await Promise.all([
        api.get(getApiUrl(`${routeBase}/${id}`), { params: { accountId, userId } }),
        api.get(getApiUrl(`${routeBase}/${id}/events`), { params: { accountId, userId } }),
      ]);
      const history = (historyRes.data?.events || []).map((e) => ({
        role: e.kind === 'agent_message' ? 'agent' : 'user',
        text: e.text,
        ts: e.ts,
        id: e.event_id,
      }));
      for (const h of history) {
        if (h.id) seenEventIds.current.add(`agent.message:${h.id}`);
      }
      if (history.length) setMessages(history);
      const priorSuggestions = metaRes.data?.sessionData?.suggestedEdits || [];
      if (priorSuggestions.length) setSuggestions(priorSuggestions);
    } catch (err) {
      console.warn(`[${logName}] history hydration failed:`, err.message);
    }
  }, [routeBase, accountId, userId, logName]);

  // ── Create — POST /. Returns the sessionId (or null on failure). ──────────
  // `resume: false` is the explicit restart ("New session"): the backend must
  // open a fresh session rather than hand back an older live one for the same
  // campaign — terminating the newest left the next-older one to resume.
  const createSession = useCallback(async ({ resume } = {}) => {
    const validationError = policyRef.current.validateCreatePayload?.(createPayload);
    if (validationError) {
      setError(validationError);
      setStatus(STATUS.ERROR);
      return null;
    }
    setStatus(STATUS.CREATING);
    setError(null);
    setArtifactErrors([]);
    try {
      // A backend deploy answers the resume attempt with a retryable 503 (the
      // alternative was worse: the old code burned the session on any error,
      // so a deploy-window 502 lost the whole conversation). Ride it out.
      let res;
      for (let attempt = 1; ; attempt++) {
        try {
          res = await api.post(getApiUrl(routeBase), {
            ...createPayload, accountId, userId, ...(resume === false ? { resume: false } : {}),
          });
          break;
        } catch (err) {
          const retryable = err?.response?.status === 503 && err?.response?.data?.retryable;
          if (!retryable || attempt >= 4) throw err;
          await new Promise((r) => setTimeout(r, 2500 * attempt));
        }
      }
      const id = res.data?.sessionId;
      if (!id) throw new Error('Backend returned no sessionId');
      if (Array.isArray(res.data?.artifactErrors) && res.data.artifactErrors.length) {
        setArtifactErrors(res.data.artifactErrors);
      }
      setSessionId(id);
      setStatus(STATUS.INITIALIZING);
      if (res.data?.resumed || res.data?.historyCarried) {
        await loadHistory(id);
      }
      const createPatch = policyRef.current.reduceCreateData?.(res.data);
      if (createPatch) {
        setPlatformState((prev) => ({
          ...prev,
          ...(typeof createPatch === 'function' ? createPatch(prev) : createPatch),
        }));
      }
      return id;
    } catch (err) {
      console.error(`[${logName}] create failed:`, err);
      setError(err?.response?.data?.error || err.message);
      setStatus(STATUS.ERROR);
      return null;
    }
  }, [routeBase, createPayload, accountId, userId, loadHistory, logName, STATUS]);

  // ── Connect — open SSE stream. Closes any prior connection. ───────────────
  const connect = useCallback((id) => {
    if (!id) return;
    if (esRef.current) esRef.current.close();
    setConnectionError(null);
    setStatus(STATUS.STREAMING);

    // EventSource can't send custom headers — pass the API key as a query
    // param. Backend middleware accepts ?apiKey= as a fallback to x-api-key.
    const apiKey = process.env.REACT_APP_API_KEY || '';
    const url = getApiUrl(
      `${routeBase}/${id}/stream`
      + `?accountId=${encodeURIComponent(accountId)}`
      + `&userId=${encodeURIComponent(userId)}`
      + `&apiKey=${encodeURIComponent(apiKey)}`,
    );
    const es = new EventSource(url);
    esRef.current = es;

    es.onmessage = (msg) => {
      let data;
      try { data = JSON.parse(msg.data); } catch { return; }
      if (!data || typeof data.type !== 'string' || !data.type) return;

      // Dedup: drop events we already replayed from history. Keyed by
      // (type, id) so request/result with the same tool_use_id don't collide.
      const dedupKey = data.id ? `${data.type}:${data.id}` : null;
      if (dedupKey && seenEventIds.current.has(dedupKey)) return;
      if (dedupKey) seenEventIds.current.add(dedupKey);

      switch (data.type) {
        case 'agent.message': {
          const text = (data.content || [])
            .filter((b) => b.type === 'text')
            .map((b) => b.text)
            .join('');
          if (text) {
            setMessages((prev) => [...prev, { role: 'agent', text, ts: Date.now(), id: data.id }]);
          }
          break;
        }
        case 'suggestion.added':
          setSuggestions((prev) => {
            const without = prev.filter((s) => s.key !== data.suggestion.key);
            return [...without, data.suggestion];
          });
          break;
        case 'apply.failed':
          // Backend's apply/commit handler returned an error (or threw).
          // Surface so the parent can flip the matching chip out of
          // 'applying'/'staged' and show the message.
          setLastApplyFailure({
            edit_key: data.edit_key || null,
            tool: data.tool || null,
            input: data.input || null,
            error: data.error || 'Apply failed for an unknown reason.',
            ts: Date.now(),
          });
          break;
        case 'edit.staged':
          // Edit landed in the agent's in-memory dict; not yet persisted.
          // Track so the chip can render 'staged' until the commit fires the
          // platform "plan updated" event.
          setLastEditStaged({
            edit_key: data.edit_key || null,
            edit: data.edit || null,
            staged_count: data.staged_count || 0,
            ts: Date.now(),
          });
          break;
        case 'session.status_idle':
          // stop_reason='requires_action' = agent needs the user; otherwise
          // it's just resting.
          setStatus(data.stop_reason === 'requires_action' ? STATUS.WAITING_FOR_USER : STATUS.STREAMING);
          setInterrupting(false);
          break;
        case 'session.status_running':
        case 'session.status_rescheduling':
          setStatus(STATUS.RUNNING);
          setInterrupting(false);
          break;
        case 'session.status_terminated':
          setStatus(STATUS.TERMINATED);
          setInterrupting(false);
          es.close();
          break;
        case 'session.finalized':
          setApprovedMarker(data.approved_marker_gcs_path);
          setStatus(STATUS.FINALIZED);
          break;
        case 'usage':
          if (data.model_usage) {
            setUsage((prev) => ({
              input_tokens: prev.input_tokens + (data.model_usage.input_tokens || 0),
              output_tokens: prev.output_tokens + (data.model_usage.output_tokens || 0),
              cache_read_input_tokens: prev.cache_read_input_tokens + (data.model_usage.cache_read_input_tokens || 0),
            }));
          }
          break;
        case 'custom_tool_request':
          // Agent kicked off a tool call. Track it so the UI knows the agent
          // is still working even while Anthropic flips status to idle for
          // the roundtrip.
          setPendingToolCalls((n) => n + 1);
          break;
        case 'custom_tool_result':
          setPendingToolCalls((n) => Math.max(0, n - 1));
          break;
        case 'error':
          if (data.transient) {
            setConnectionError(data.message);
          } else {
            setError(data.message);
            setStatus(STATUS.ERROR);
          }
          break;
        default:
          // Ignore agent.tool_use, raw.*, etc. The platform "plan updated"
          // event is handled by reducePlatformEvent below.
          break;
      }

      // Platform-specific state (review_cts.updated / final_plan.updated, and
      // any extra fields the platform layers onto session.finalized). Run for
      // every event; the reducer returns null for ones it doesn't own.
      const patch = policyRef.current.reducePlatformEvent?.(data);
      if (patch) {
        setPlatformState((prev) => ({
          ...prev,
          ...(typeof patch === 'function' ? patch(prev) : patch),
        }));
      }
    };

    es.onopen = () => {
      reconnectAttempts.current = 0;
      setConnectionError(null);
    };
    es.onerror = () => {
      // EventSource auto-reconnects on transient errors. For fatal closes
      // (4xx, LB 502 during a backend deploy), readyState becomes CLOSED and
      // the browser stops retrying — retry OURSELVES with backoff before
      // asking for a manual click, because a deploy window outlives a single
      // attempt and 'click Reconnect' mid-deploy just fails again (UAT 0819).
      if (es.readyState !== EventSource.CLOSED) return;
      const attempt = (reconnectAttempts.current || 0) + 1;
      reconnectAttempts.current = attempt;
      if (attempt <= 6) {
        setConnectionError(`Stream dropped — reconnecting (attempt ${attempt}/6)…`);
        const delay = Math.min(2000 * attempt, 12000);
        setTimeout(() => {
          // Only if this ES is still the active one — a newer connection or a
          // torn-down session must not be fought by a zombie retry.
          if (esRef.current === es) connect(id);
        }, delay);
      } else {
        setConnectionError('Stream closed — click Reconnect to resume.');
      }
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [routeBase, accountId, userId, STATUS]);

  const reconnect = useCallback(() => {
    if (!sessionId) return;
    connect(sessionId);
  }, [sessionId, connect]);

  // ── Actions ───────────────────────────────────────────────────────────────
  // `idOverride` covers the sidebar's on-demand bootstrap: createSession()
  // just resolved with a fresh id, but this callback still closes over the
  // pre-creation sessionId (null) until the state update lands — without
  // the override the first message would be silently dropped.
  const sendMessage = useCallback(async (text, idOverride = null) => {
    const sid = idOverride || sessionId;
    if (!sid || !text?.trim()) return;
    setMessages((prev) => [...prev, { role: 'user', text, ts: Date.now() }]);
    try {
      await api.post(getApiUrl(`${routeBase}/${sid}/message`), { text, accountId, userId });
    } catch (err) {
      // Self-heal a ZOMBIE tool wait. Custom tools are executed by the SSE
      // stream handler; if that dies mid-call (deploy, tab close), the remote
      // session waits forever on a tool result nobody will ever send — every
      // message then 409s "agent is mid-tool-call" and the session looks
      // dead until the user gives up and creates a new one (UAT 0819). When
      // the backend says mid-tool but NOTHING is running locally, the wait is
      // a zombie by definition: interrupt to clear it and retry once. A
      // genuine in-flight tool (local count > 0) keeps the old behaviour.
      const zombieToolWait = err?.response?.status === 409
        && err?.response?.data?.pendingTool
        && pendingToolCallsRef.current === 0;
      if (zombieToolWait) {
        try {
          await api.post(getApiUrl(`${routeBase}/${sid}/interrupt`), { accountId, userId });
          await new Promise((r) => setTimeout(r, 1500));
          await api.post(getApiUrl(`${routeBase}/${sid}/message`), { text, accountId, userId });
          return;
        } catch (retryErr) {
          setError(retryErr?.response?.data?.error || retryErr.message);
          return;
        }
      }
      setError(err?.response?.data?.error || err.message);
    }
  }, [routeBase, sessionId, accountId, userId]);

  // Adapter escape hatches, for platform state an adapter changes in response to
  // the USER rather than to a stream event (the GroundTruth bulk editor marks a
  // proposal "applying" when Confirm lands, and records a choice-card pick).
  // Unused by the reviewer adapters, whose state only ever moves on events.
  const updatePlatformState = useCallback((patchOrFn) => {
    setPlatformState((prev) => {
      const patch = typeof patchOrFn === 'function' ? patchOrFn(prev) : patchOrFn;
      return patch ? { ...prev, ...patch } : prev;
    });
  }, []);
  // A user turn that reached the agent through an adapter-specific route (the
  // bulk editor's /choose) rather than /message — shown in the feed exactly like
  // a sent message. Returns the stamped message.
  const pushLocalMessage = useCallback((msg) => {
    const stamped = { role: 'user', ts: Date.now(), ...msg };
    setMessages((prev) => [...prev, stamped]);
    return stamped;
  }, []);

  const interrupt = useCallback(async () => {
    if (!sessionId) return;
    // Keep `interrupting` true until a terminal status event clears it (the
    // idle/running/terminated handlers call setInterrupting(false)). A 20s
    // safety timer clears it if Anthropic never emits a status event.
    setInterrupting(true);
    const safetyTimer = setTimeout(() => setInterrupting(false), 20000);
    try {
      await api.post(getApiUrl(`${routeBase}/${sessionId}/interrupt`), { accountId, userId });
    } catch (err) {
      console.warn(`[${logName}] interrupt failed:`, err.message);
      setError(err?.response?.data?.error || err.message);
      clearTimeout(safetyTimer);
      setInterrupting(false);
    }
  }, [routeBase, sessionId, accountId, userId, logName]);

  const terminate = useCallback(async () => {
    if (!sessionId) return;
    try {
      await api.post(getApiUrl(`${routeBase}/${sessionId}/terminate`), { accountId, userId });
      setStatus(STATUS.TERMINATED);
      if (esRef.current) esRef.current.close();
    } catch (err) {
      console.warn(`[${logName}] terminate failed:`, err.message);
    }
  }, [routeBase, sessionId, accountId, userId, logName, STATUS]);

  // Restart — tear down the current session and open a fresh one. The
  // "New session" button in the sidebar calls this. Resets the
  // conversation-facing state (messages, suggestions, usage, status, dedup
  // set) only; the platform/edit state (platformState — latest* paths,
  // approved markers) is left intact so committed edits survive the restart.
  const newSession = useCallback(async () => {
    if (sessionId) {
      try { await terminate(); } catch { /* best-effort — recreate regardless */ }
    }
    if (esRef.current) { esRef.current.close(); esRef.current = null; }
    seenEventIds.current = new Set();
    setMessages([]);
    setSuggestions([]);
    setError(null);
    setConnectionError(null);
    setInterrupting(false);
    setPendingToolCalls(0);
    setUsage(ZERO_USAGE);
    setArtifactErrors([]);
    setSessionId(null);
    const id = await createSession({ resume: false });
    if (id) connect(id);
    // Returned so a caller can send INTO the new session immediately. The
    // `sessionId` state update has not landed yet at this point, so a follow-up
    // sendMessage() would otherwise close over the old (or null) id and be
    // delivered to the wrong session — the same reason sendMessage takes an
    // idOverride for the bootstrap path.
    return id;
  }, [sessionId, terminate, createSession, connect]);

  // ── The subject changed — drop the session that was about the old one ─────
  // Only fires when the caller opted into sessionKey AND a session for a
  // DIFFERENT key is live. The first key an instance sees is recorded, not
  // treated as a change.
  //
  // This CLEARS but does not create. The new subject's payload usually is not
  // loaded yet at this instant (on the optimizer, `campaignId` changes a beat
  // before the new campaign's run and digest arrive), so creating here would
  // open a session for the new campaign carrying the old campaign's digest —
  // the same crossed-wires bug in a new place. Clearing bumps restartToken,
  // which re-arms auto-start below; auto-start then creates as soon as
  // isAutoStartReady passes for the NEW payload, or leaves the surface in its
  // no-run state if that campaign has nothing to talk about yet.
  const sessionKeyRef = useRef(sessionKey);
  const [restartToken, setRestartToken] = useState(0);
  useEffect(() => {
    if (sessionKey === sessionKeyRef.current) return;
    const previousKey = sessionKeyRef.current;
    sessionKeyRef.current = sessionKey;
    if (previousKey === null || sessionKey === null || !sessionId) return;
    console.info(
      `[${logName}] session subject changed (${previousKey} -> ${sessionKey}); starting a new conversation`,
    );
    // State first, in one batch, so the stale conversation is off screen
    // immediately and auto-start sees sessionId === null on the next commit.
    // The server-side terminate rides along in the background: it is
    // best-effort cleanup of the OLD id, and waiting on it would leave the
    // previous campaign's messages on screen for the length of a round trip.
    const staleId = sessionId;
    if (esRef.current) { esRef.current.close(); esRef.current = null; }
    seenEventIds.current = new Set();
    setMessages([]);
    setSuggestions([]);
    setError(null);
    setConnectionError(null);
    setInterrupting(false);
    setPendingToolCalls(0);
    setUsage(ZERO_USAGE);
    setArtifactErrors([]);
    setPlatformState(initialPlatformStateRef.current);
    setStatus(STATUS.IDLE);
    setSessionId(null);
    setRestartToken((t) => t + 1);
    api.post(getApiUrl(`${routeBase}/${staleId}/terminate`), { accountId, userId })
      .catch((err) => console.warn(`[${logName}] terminate of replaced session failed:`, err.message));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [sessionKey]);

  // ── Auto-start ────────────────────────────────────────────────────────────
  // restartToken is in the dep list so a subject change re-arms this. It never
  // moves for callers that don't pass sessionKey, so their behaviour is
  // unchanged.
  useEffect(() => {
    if (!autoStart) return;
    if (sessionId) return;
    if (!policyRef.current.isAutoStartReady?.(createPayload)) return;
    (async () => {
      const id = await createSession();
      if (id) connect(id);
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [autoStart, restartToken, ...autoStartDeps]);

  // Cleanup on unmount
  useEffect(() => () => {
    if (esRef.current) esRef.current.close();
  }, []);

  // True while the session is COMING UP — a create in flight, history
  // hydrating, or auto-start armed with a ready payload but the id not landed
  // yet. The sidebar shows a loading state off this instead of an empty feed,
  // which is what the first few seconds of the bulk editor used to look like.
  //
  // The isAutoStartReady term is what keeps it honest: on a surface where the
  // payload will never be ready (no rows yet, wrong destination) auto-start
  // never fires, so this stays false and the sidebar shows its fallback rather
  // than spinning forever on a session nothing is going to open.
  const starting = status === STATUS.CREATING
    || status === STATUS.INITIALIZING
    || (autoStart && !sessionId && status !== STATUS.ERROR
      && Boolean(policyRef.current.isAutoStartReady?.(createPayload)));

  return {
    sessionId,
    messages,
    suggestions,
    status,
    starting,
    error,
    connectionError,
    approvedMarker,
    usage,
    artifactErrors,
    interrupting,
    pendingToolCalls,
    lastApplyFailure,
    lastEditStaged,
    ...platformState,
    createSession,
    connect,
    reconnect,
    sendMessage,
    interrupt,
    terminate,
    newSession,
    updatePlatformState,
    pushLocalMessage,
    STATUS,
  };
}
